import React, { useEffect, useRef, useState } from 'react';
import { Location } from '../../types';
import { notification } from 'antd';

const { GOOGLE_MAP_API_KEY } = require('../../core/constants').default;

const DEFAULT_HEIGHT: string = '32px';
const DEFAULT_FONT_SIZE: string = '12px';

interface GooglePlacesAutocompleteProps {
    onPlaceSelected: (location: Location) => void;
    style?: React.CSSProperties;
    isVisible?: boolean;
}

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({ 
  onPlaceSelected, 
  style = {},
  isVisible = true,
}) => {
    const autocompleteRef = useRef(null);

    const [ isScriptLoaded, setIsScriptLoaded ] = useState<boolean>(false);
    const [ focus, setFocus ] = useState<boolean>(false);
    const [ selectedPlace, setSelectedPlace ] = useState<Location | null>(null);

    useEffect(() => {
        // Mega hack the shadow root to enforce new styles
        //   can be removed when new Autocomplete component support styling via config etc.
        // https://stackoverflow.com/a/79590414/5640115
        const attachShadow = Element.prototype.attachShadow;
        Element.prototype.attachShadow = function (init: ShadowRootInit) {
            // Check if we are the new Google places autocomplete element...
            if (this.localName === "gmp-place-autocomplete") {
                // If we are, we need to override the default behaviour of attachShadow() to
                // set the mode to open to allow us to crowbar a style element into the shadow DOM.
                const shadow = attachShadow.call(this, {
                    ...init,
                    mode: "open",
                });
                const style = document.createElement("style");
                style.textContent = `
                    .widget-container {
                        border: 1px solid var(--cool-gray-20) !important;
                        max-width: 210px !important;
                        min-width: 120px !important;
                        height: 24px !important;
                        max-height: 24px !important;
                        min-height: 24px !important;

                        .input-container {
                            overflow: visible !important;
                            background-color: none !important;
                            
                            input {
                                margin-top: -8px !important;
                                font-size: 12px !important;
                                height: 20px !important;
                                max-height: 20px !important;
                                min-height: 20px !important;
                                background-color: #00000000 !important;
                            }
                        }

                        
                    }
                    .focus-ring {
                        border: none !important;
                    }
                `;
                shadow.appendChild(style);
                return shadow;
            }
            // for other elements, proceed with the original behaviour of attachShadow().
            return attachShadow.call(this, init);
        };
    }, []);
    useEffect(() => {
        if (window.google?.maps?.importLibrary) {
            setIsScriptLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_API_KEY}&libraries=places&v=weekly&callback=initGoogleMaps`;
        script.async = true;
        script.defer = true;
        window.initGoogleMaps = () => { setIsScriptLoaded(true); };
        document.head.appendChild(script);

        return () => {
            const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
            if (existingScript) {
                document.head.removeChild(existingScript);
            }
            if (window.initGoogleMaps) {
                delete window.initGoogleMaps;
            }
        };
    }, [GOOGLE_MAP_API_KEY]);
    useEffect(() => {
        if (!isScriptLoaded) {
            return;
        }

        const initializeAutocomplete = async () => {
            try {
                await window.google.maps.importLibrary("places");
                const placeAutocomplete = new window.google.maps.places.PlaceAutocompleteElement({});
                placeAutocomplete.style.fontSize = DEFAULT_FONT_SIZE;
                placeAutocomplete.style.height = DEFAULT_HEIGHT - 2;
                placeAutocomplete.placeholder = 'Search Address / Postcode';
                if (autocompleteRef.current) {
                    autocompleteRef.current.innerHTML = '';
                    autocompleteRef.current.appendChild(placeAutocomplete);
                    placeAutocomplete.addEventListener('gmp-select', async ({ placePrediction }) => {
                        const place = placePrediction.toPlace();
                        await place.fetchFields({ fields: ['location'] });
                        const newPlace = {
                          lat: place.location.lat(), 
                          lng: place.location.lng(),
                        };
                        setSelectedPlace(newPlace);
                        onPlaceSelected(newPlace);
                    });
                }
            } catch (err) {
                notification.error({
                    message: 'Error',
                    description: 'Google Maps failed to load properly, please try refreshing the page',
                });
                throw new Error('Google Maps failed to load properly, please try refreshing the page');
            }
        };
        initializeAutocomplete();
    }, [isScriptLoaded]);

    return (
        <>
            <div 
                ref={autocompleteRef} 
                className="border border-gray-300 rounded-md p-2 min-h-12 bg-gray-50"
                style={{
                    ...style,
                    height: DEFAULT_HEIGHT,
                    marginLeft: '16px',
                    marginRight: '16px',
                }}
                onFocus={() => { setFocus(true) }}
                onBlur={(e) => { 
                    setFocus(false);
                    setSelectedPlace({
                        lat: 0,
                        lng: 0
                    }); // tricks the placeholder to stay hidden
                }}
            />
            {isVisible && !focus && !selectedPlace && (
                <p style={{
                    position: 'absolute',
                    top: 6,
                    left: 8,
                    fontSize: '12px',
                    backgroundColor: '#00000000',
                    color: 'var(--cool-gray-60)',
                    pointerEvents: 'none'
                }}>
                    Search Address/Postcode
                </p>
            )}
            
        </>
    );
};

export default GooglePlacesAutocomplete;